import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { Receipt, Wallet, Plus } from "lucide-react";
import type { InvoiceListItemDto } from "@procureflow/shared-types";
import {
  TransactionListShell,
  type ColumnDef,
  type StatusTab,
} from "@/components/transaction-list-shell";
import { formatAmount, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";

// US-034 — Invoice list page. Replaces the Lovable v1.1 deferral
// placeholder with a real list rendered from GET /api/invoices.
// Status tabs cover the entire InvoiceStatus enum so Finance can
// narrow by lifecycle state from one page.

export const Route = createFileRoute("/invoices")({
  head: () => ({
    meta: [
      { title: "Invoices — ProcureFlow" },
      { name: "description", content: "Browse invoices across the full lifecycle." },
    ],
  }),
  component: function InvoicesPageOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <InvoiceListPage />;
  },
});

const TABS: StatusTab[] = [
  { label: "All", value: null },
  { label: "Recorded", value: "Recorded" },
  { label: "Matched", value: "Matched" },
  { label: "Exception", value: "Exception" },
  { label: "Held", value: "Held" },
  { label: "Approved", value: "Approved" },
  { label: "Paid", value: "Paid" },
  { label: "Rejected", value: "Rejected" },
  { label: "Cancelled", value: "Cancelled" },
];

const COLUMNS: ColumnDef<InvoiceListItemDto>[] = [
  {
    id: "invoice_number",
    header: "Invoice #",
    cell: (inv) => (
      <Link to="/invoices/$id" params={{ id: inv.id }} className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
          <Receipt className="h-3.5 w-3.5" />
        </div>
        <span className="font-mono text-xs font-medium hover:text-primary transition-colors">
          {inv.invoice_number}
        </span>
      </Link>
    ),
  },
  {
    id: "vendor_invoice",
    header: "Vendor Invoice #",
    cell: (inv) => (
      <span className="font-mono text-xs text-muted-foreground">{inv.vendor_invoice_number}</span>
    ),
  },
  {
    id: "vendor",
    header: "Vendor",
    cell: (inv) => (
      <span className="font-medium truncate max-w-[220px] block">{inv.vendor_name}</span>
    ),
  },
  {
    id: "po_number",
    header: "PO #",
    cell: (inv) => <span className="font-mono text-xs text-muted-foreground">{inv.po_number}</span>,
  },
  {
    id: "source_gr",
    header: "Source GR(s)",
    // US-066 — an invoice built from goods receipts lists each distinct source
    // GR number; a manually-recorded invoice has no GR origin.
    cell: (inv) => (
      <span className="font-mono text-xs text-muted-foreground">
        {inv.source_gr_numbers && inv.source_gr_numbers.length > 0
          ? inv.source_gr_numbers.join(", ")
          : "—"}
      </span>
    ),
  },
  {
    id: "invoice_date",
    header: "Invoice Date",
    cellClassName: "text-muted-foreground",
    cell: (inv) => formatDate(inv.invoice_date),
  },
  {
    id: "due_date",
    header: "Due Date",
    cellClassName: "text-muted-foreground",
    cell: (inv) => formatDate(inv.due_date),
  },
  {
    id: "total_amount",
    header: "Amount",
    headerClassName: "text-right",
    cellClassName: "text-right tabular-nums font-medium",
    cell: (inv) => formatAmount(inv.total_amount),
  },
];

function InvoiceListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // US-066 — Create an invoice from one or more goods receipts. The builder
  // POSTs /api/invoices/from-grns (Finance / ProcurementOfficer / Admin-only);
  // gate the entry point to match.
  const canCreateInvoice =
    user?.role === "Finance" || user?.role === "ProcurementOfficer" || user?.role === "Admin";
  return (
    <div data-testid="invoice-list-section">
      <TransactionListShell<InvoiceListItemDto>
        title="Invoices"
        countNoun={{ singular: "invoice", plural: "invoices" }}
        endpoint="/api/invoices"
        tabs={TABS}
        columns={COLUMNS}
        rowKey={(inv) => inv.id}
        rowClassName="cursor-pointer hover:bg-muted/30 transition-colors"
        onRowClick={(inv) => navigate({ to: "/invoices/$id", params: { id: inv.id } })}
        emptyIcon={Wallet}
        emptyMessage="No invoices match your filters."
        headerAction={
          canCreateInvoice ? (
            <Button asChild data-testid="create-invoice-from-gr-button">
              <Link to="/invoices/from-grns" search={{ grId: "", editId: "" }}>
                <Plus className="h-3.5 w-3.5" /> Create Invoice from GR
              </Link>
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
